/**
 * THE metrics (Step 16): every number on the dashboard and the analytics pages is defined here,
 * with its exact formula, its denominator and its kind. Browser-safe: the API computes with
 * these, the web app shows `formula` in the ⓘ tooltip. Change a definition here only.
 *
 * Kinds:
 * - time:   events in the period / leads contacted in the period (trends, Overview KPIs).
 * - cohort: the leads contacted in the period; their replies, positive replies and
 *           opportunities counted whenever they happened (comparisons). Leads contacted in the
 *           last 7 days are still collecting replies (YOUNG_DAYS).
 * - state:  how things are right now (inbox estimates, open opportunities, mailboxes).
 *
 * Only campaign email counts: warm-up, seed, placement and dry-run sends never do.
 */
export type MetricKind = 'time' | 'cohort' | 'state';
export type MetricFormat = 'count' | 'percent' | 'per1000';

export interface MetricDef {
  /** Plain words (main screens use these). */
  label: string;
  /** One simple sentence: the first line of the tooltip. */
  explain: string;
  /** The exact formula: the tooltip's second line. */
  formula: string;
  kind: MetricKind;
  format: MetricFormat;
  /** Which way is good (colours the change); null = neither. */
  good: 'up' | 'down' | null;
  /** Below this denominator the value is "Not enough data". */
  minSample?: number;
  /** Warning / problem lines (rates as fractions). */
  thresholds?: { warn: number; bad: number };
  /** Built from the hourly rollups, so it can trail a live number elsewhere. */
  hourly: boolean;
}

/** Leads contacted in the last 7 days are still collecting replies. */
export const YOUNG_DAYS = 7;
/** Minimum samples (decision 10). */
export const MIN_RATE_SAMPLE = 50;
export const MIN_SEGMENT_SAMPLE = 30;
export const MIN_VARIANT_SAMPLE = 100;

const NOTE_TIME = 'What happened in the period ÷ people emailed in the period.';
const NOTE_COHORT =
  'The people emailed in the period, with their replies counted whenever they came. People emailed in the last 7 days may still reply, so they are shown apart.';

export const METRICS = {
  emails_sent: {
    label: 'Emails sent',
    explain: 'How many campaign emails went out.',
    formula: 'Campaign emails sent (warm-up, seed and dry-run emails are not counted).',
    kind: 'time',
    format: 'count',
    good: null,
    hourly: true,
  },
  leads_contacted: {
    label: 'People emailed',
    explain: 'How many different people got their first email from a campaign.',
    formula: 'Different people who got their first campaign email in the period.',
    kind: 'time',
    format: 'count',
    good: 'up',
    hourly: true,
  },
  leads_replied: {
    label: 'Replied',
    explain:
      'People who wrote back, each counted once per campaign however often they wrote (out-of-office and other automatic replies do not count).',
    formula:
      'People whose first reply in a campaign came in the period (a reply linked to no campaign: once per sender; automatic replies not counted).',
    kind: 'time',
    format: 'count',
    good: 'up',
    hourly: true,
  },
  reply_rate: {
    label: 'Reply rate',
    explain: 'Out of the people emailed, the share who wrote back.',
    formula: `People who replied ÷ people emailed. ${NOTE_TIME}`,
    kind: 'time',
    format: 'percent',
    good: 'up',
    minSample: MIN_RATE_SAMPLE,
    hourly: true,
  },
  interested_replies: {
    label: 'Interested replies',
    explain: 'Replies sorted as Interested or Meeting request.',
    formula: 'Replies sorted as Interested or Meeting request in the period.',
    kind: 'time',
    format: 'count',
    good: 'up',
    hourly: true,
  },
  leads_interested: {
    label: 'Interested',
    explain: 'People who replied that they are interested or want a meeting.',
    formula:
      'Different people with a reply sorted as Interested or Meeting request in the period (each person counted once).',
    kind: 'time',
    format: 'count',
    good: 'up',
    hourly: true,
  },
  positive_reply_rate: {
    label: 'Interested rate',
    explain: 'Out of the people emailed, the share who said they are interested.',
    formula: `People with an Interested or Meeting request reply ÷ people emailed. ${NOTE_TIME}`,
    kind: 'time',
    format: 'percent',
    good: 'up',
    minSample: MIN_RATE_SAMPLE,
    hourly: true,
  },
  opportunities: {
    label: 'Opportunities',
    explain: 'Interested people who became a sales opportunity for your team.',
    formula: 'Opportunities created in the period.',
    kind: 'time',
    format: 'count',
    good: 'up',
    hourly: true,
  },
  opportunity_rate: {
    label: 'Opportunity rate',
    explain: 'Out of the people emailed, the share who became an opportunity.',
    formula: `Opportunities ÷ people emailed. ${NOTE_TIME}`,
    kind: 'time',
    format: 'percent',
    good: 'up',
    minSample: MIN_RATE_SAMPLE,
    hourly: true,
  },
  opportunities_per_1000: {
    label: 'Opportunities per 1,000 emails',
    explain: 'Opportunities for every 1,000 emails sent.',
    formula: 'Opportunities ÷ emails sent × 1,000.',
    kind: 'time',
    format: 'per1000',
    good: 'up',
    minSample: 1000,
    hourly: true,
  },
  bounce_rate: {
    label: 'Bounced',
    explain: 'Emails that could not be delivered, usually because the address does not exist.',
    formula: 'Hard bounces ÷ emails sent. Warning at 2%, campaigns pause at 3%.',
    kind: 'time',
    format: 'percent',
    good: 'down',
    minSample: 100,
    thresholds: { warn: 0.02, bad: 0.03 },
    hourly: true,
  },
  complaint_rate: {
    label: 'Spam complaints',
    explain: 'Emails that the person marked as spam.',
    formula: 'Spam complaints ÷ emails sent. Warning at 0.1%, sending pauses at 0.3%.',
    kind: 'time',
    format: 'percent',
    good: 'down',
    minSample: 100,
    thresholds: { warn: 0.001, bad: 0.003 },
    hourly: true,
  },
  unsubscribe_rate: {
    label: 'Unsubscribed',
    explain: 'People who asked not to get more emails.',
    formula: `People who unsubscribed ÷ people emailed. ${NOTE_TIME}`,
    kind: 'time',
    format: 'percent',
    good: 'down',
    minSample: MIN_RATE_SAMPLE,
    hourly: true,
  },
  est_inbox_rate: {
    label: 'Inbox estimate',
    explain: 'Our estimate of how much campaign email lands in the inbox instead of spam.',
    formula:
      'Estimated share of campaign email landing in the inbox (seed tests and signals, with a confidence range). How it is now, not over the period.',
    kind: 'state',
    format: 'percent',
    good: 'up',
    hourly: true,
  },
  capacity_used: {
    label: 'Capacity used',
    explain: 'How much of what your mailboxes can safely send each day was used.',
    formula: 'Campaign emails sent ÷ the daily capacity of the mailboxes (limits × health level).',
    kind: 'time',
    format: 'percent',
    good: null,
    hourly: true,
  },
  cohort_reply_rate: {
    label: 'Reply rate',
    explain:
      'Out of the people emailed in the period, the share who replied (whenever they replied).',
    formula: `People emailed who replied ÷ people emailed. ${NOTE_COHORT}`,
    kind: 'cohort',
    format: 'percent',
    good: 'up',
    minSample: MIN_SEGMENT_SAMPLE,
    hourly: true,
  },
  cohort_positive_rate: {
    label: 'Interested rate',
    explain: 'Out of the people emailed in the period, the share who said they are interested.',
    formula: `People emailed with an Interested or Meeting request reply ÷ people emailed. ${NOTE_COHORT}`,
    kind: 'cohort',
    format: 'percent',
    good: 'up',
    minSample: MIN_SEGMENT_SAMPLE,
    hourly: true,
  },
  cohort_opportunity_rate: {
    label: 'Opportunity rate',
    explain: 'Out of the people emailed in the period, the share who became an opportunity.',
    formula: `People emailed who became an opportunity ÷ people emailed. ${NOTE_COHORT}`,
    kind: 'cohort',
    format: 'percent',
    good: 'up',
    minSample: MIN_SEGMENT_SAMPLE,
    hourly: true,
  },
  source_valid_rate: {
    label: 'Valid',
    explain: 'People whose email address was checked and exists.',
    formula: `People emailed whose address check says valid ÷ people emailed with a check result. ${NOTE_COHORT}`,
    kind: 'cohort',
    format: 'percent',
    good: 'up',
    minSample: MIN_SEGMENT_SAMPLE,
    hourly: true,
  },
  source_bounce_rate: {
    label: 'Bounced',
    explain: 'People whose email bounced.',
    formula: `People emailed whose email hard-bounced ÷ people emailed. ${NOTE_COHORT}`,
    kind: 'cohort',
    format: 'percent',
    good: 'down',
    minSample: MIN_SEGMENT_SAMPLE,
    hourly: true,
  },
} as const satisfies Record<string, MetricDef>;

export type MetricId = keyof typeof METRICS;

/** The reply types shown (Audience: what people say); others count as "Other". */
export const REPORTED_CATEGORIES = [
  'Interested',
  'Meeting request',
  'Not interested',
  'Out of office',
  'Wrong person',
  'Unsubscribe request',
] as const;
